import {
  Alert,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { format, parseISO } from 'date-fns'
import { useState } from 'react'
import {
  useAddOrderSession,
  useApplyOrderCharges,
  useEditOrder,
  useEditOrderSession,
  useHallSlotBoard,
  useHalls,
  usePackages,
  usePaxLimits,
  useServiceTypes,
  useSessionTypes,
} from '@/api/hooks'
import type { DeliveryAddress, EventSession, OrderDetail } from '@/api/types'
import { ErrorBanner, formatMoney, formatSlotHours, TimeField } from '@/components/common'
import { BLANK_DELIVERY_ADDRESS, DeliveryAddressFields } from '@/components/DeliveryAddressFields'
import { DiscountField } from '@/components/DiscountField'
import { PackageMenuChoices, type MenuChoice } from '@/pages/orders/PackageMenuChoices'

/**
 * The order this is entered in, which is the order it is asked for in.
 *
 * <p>A session is agreed as a conversation: when it runs and for how many, then
 * what they are being served, then what it comes to. It walks the same three
 * stages as taking the order does - adding one and correcting one alike - so the
 * same questions are asked in the same order however a booking arrives and
 * whenever it is looked at again.</p>
 */
const STAGES = ['Session and pax', 'Package and menu', 'Charges and terms']

/** A datetime-local input wants the local wall clock, not an offset. */
function toLocalInput(iso: string) {
  return format(parseISO(iso), "yyyy-MM-dd'T'HH:mm")
}

/**
 * A sitting of an order: moving one, or putting the first one in.
 *
 * <p>Changing the hall or the hours re-checks the booking against every other
 * one, so an order cannot be edited into a double booking. The order is not
 * treated as a rival to itself: a customer's own breakfast never blocks their
 * own lunch. A conflict with somebody else can be pushed past only by giving a
 * reason, which is recorded on the booking.</p>
 *
 * <p>The same form adds one. An order taken at the counter arrives with a date
 * and no sittings; adding one here is what puts it in the diary and, where a
 * hall is named, what holds the hall.</p>
 */
export function SessionEditDialog(props: {
  order: OrderDetail
  session: EventSession | null
  /** Open with an empty form, to put a sitting on an order that has none. */
  adding?: boolean
  onClose: () => void
}) {
  const { order, session } = props
  const adding = Boolean(props.adding) && !session
  const edit = useEditOrderSession()
  const add = useAddOrderSession()
  const charges = useApplyOrderCharges()
  const editOrder = useEditOrder()
  const { data: halls } = useHalls(order.outletId)
  // Which session this is - breakfast, lunch, dinner. It was never asked for
  // here, so a session added on an order carried none and the kitchen sheet had
  // nothing to sort by.
  const { data: sessionTypes } = useSessionTypes({ serviceType: order.serviceType })
  // What this line of business sells by the head. A package with no version
  // behind it has no price to bring, so it is not offered.

  const [eventName, setEventName] = useState('')
  const [hallId, setHallId] = useState('')
  const [hallSlotId, setHallSlotId] = useState('')
  const [eventStart, setEventStart] = useState('')
  const [eventEnd, setEventEnd] = useState('')
  const [guestCount, setGuestCount] = useState('')
  const [packageVersionId, setPackageVersionId] = useState('')
  const [pricePerPerson, setPricePerPerson] = useState('')
  const [overrideReason, setOverrideReason] = useState('')
  const [mealPeriod, setMealPeriod] = useState('')
  const [serviceStyle, setServiceStyle] = useState('')
  // Where the food is going. Kept in parts rather than as a line of text: a
  // driver looking for a reception at eight in the evening needs the door, the
  // turning and something to recognise it by.
  const [address, setAddress] = useState<DeliveryAddress>(BLANK_DELIVERY_ADDRESS)
  // Beside the address in parts: the sentence that is not an address line, how
  // far it is, and the pin. The last two are what the trip is planned on.
  const [findingIt, setFindingIt] = useState('')
  const [venueDistanceKm, setVenueDistanceKm] = useState('')
  const [venueMapUrl, setVenueMapUrl] = useState('')
  // The job's, not this sitting's, and asked here because this is the form the
  // office is on when it learns them.
  const [fulfilmentMode, setFulfilmentMode] = useState('DELIVERY')
  const [manpowerRequired, setManpowerRequired] = useState(true)
  // What the customer picked off the package. Null until a package has been
  // chosen and its lines read, which is not the same as picking nothing.
  const [menuChoices, setMenuChoices] = useState<MenuChoice[] | null>(null)
  const [stage, setStage] = useState(0)
  // Charges belong to the order rather than to this session, and are asked for
  // here because this is where the conversation ends.
  const [discountPercent, setDiscountPercent] = useState('')
  const [discountReason, setDiscountReason] = useState('')
  const [discountRecommendedBy, setDiscountRecommendedBy] = useState('')
  const [notes, setNotes] = useState('')
  // What the charges said when the form opened. Saving writes them only if
  // somebody changed them: the last stage shows what the order already carries,
  // and re-posting that on every session save would reprice an order nobody
  // asked to reprice.
  const [chargesAsFound, setChargesAsFound] = useState({
    discountPercent: '',
    discountReason: '',
    discountRecommendedBy: '',
    notes: '',
  })

  // For this order's branch and this sitting's hall, not for the whole company:
  // a menu written against another hall is not one this sitting can be sold on.
  const { data: packages } = usePackages({
    serviceType: order.serviceType,
    // Breakfast menus for a breakfast. A package is written for a sitting of
    // the day, and offering the dinner menu against a breakfast was how a
    // sitting got sold on food the kitchen does not cook at that hour. One
    // written for any sitting is still offered, because that is what it says.
    sessionType: mealPeriod || undefined,
    outletId: order.outletId,
    hallId: hallId || undefined,
  })
  const sellable = (packages ?? []).filter((one) => one.currentVersionId)
  const [loaded, setLoaded] = useState<string | null>(null)

  /**
   * The hall this order is already in.
   *
   * <p>It is chosen once when the order is taken, and every sitting of the day
   * is in it. A sitting added afterwards takes it rather than asking again -
   * nobody books the reception in one hall and the dinner in another by
   * accident, and being asked invites exactly that.</p>
   */
  const orderHallId = order.sessions.find((one) => one.hallId)?.hallId ?? ''

  /** Where this order is already going, if any sitting of it says. */
  const orderAddress = order.sessions.find((one) => one.address)?.address ?? null

  // A blank form, defaulted from the order: its day, its occasion and the head
  // count it was taken for are the answers most of the time.
  if (adding && loaded !== 'new') {
    setLoaded('new')
    setEventName(order.eventType ?? '')
    setHallId(orderHallId)
    setHallSlotId('')
    setEventStart(`${order.eventDate}T12:00`)
    setEventEnd(`${order.eventDate}T16:00`)
    setGuestCount(String(order.guestCount || ''))
    setPackageVersionId('')
    setPricePerPerson('')
    setOverrideReason('')
    setMealPeriod('')
    setServiceStyle('')
    // Every sitting of one function is at one place unless somebody says
    // otherwise, so a second sitting starts from where the first is going.
    setAddress(orderAddress ?? BLANK_DELIVERY_ADDRESS)
    setFindingIt('')
    setVenueDistanceKm('')
    setVenueMapUrl('')
    setFulfilmentMode(order.fulfilmentMode ?? 'DELIVERY')
    setManpowerRequired(order.manpowerRequired ?? true)
    setMenuChoices(null)
    setStage(0)
    setDiscountPercent('')
    setDiscountReason('')
    setDiscountRecommendedBy('')
    setNotes('')
    setChargesAsFound({
      discountPercent: '',
      discountReason: '',
      discountRecommendedBy: '',
      notes: '',
    })
  }

  // Fill the form from the sitting the first time it is opened for it.
  if (session && loaded !== session.id) {
    setLoaded(session.id)
    setEventName(session.eventName ?? '')
    setHallId(session.hallId ?? '')
    setHallSlotId(session.hallSlotId ?? '')
    setEventStart(toLocalInput(session.eventStart))
    setEventEnd(toLocalInput(session.eventEnd))
    setGuestCount(String(session.guestCount))
    setPackageVersionId(session.packageVersionId ?? '')
    setPricePerPerson(session.pricePerPerson == null ? '' : String(session.pricePerPerson))
    setOverrideReason('')
    setMealPeriod(session.mealPeriod ?? '')
    setServiceStyle(session.serviceStyle ?? '')
    setAddress(session.address ?? orderAddress ?? BLANK_DELIVERY_ADDRESS)
    // Only where it is not simply the address written out again: the composed
    // line is what the parts already say.
    setFindingIt(session.address ? '' : (session.deliveryAddress ?? ''))
    setVenueDistanceKm(session.venueDistanceKm == null ? '' : String(session.venueDistanceKm))
    setVenueMapUrl(session.venueMapUrl ?? '')
    setFulfilmentMode(order.fulfilmentMode ?? 'DELIVERY')
    setManpowerRequired(order.manpowerRequired ?? true)
    setMenuChoices(null)
    setStage(0)
    // What the order already carries, so the last stage shows what was agreed
    // rather than an empty form that would read as nothing having been given.
    setDiscountPercent('')
    setDiscountReason(order.discountReason ?? '')
    setDiscountRecommendedBy(order.discountRecommendedBy ?? '')
    setNotes(order.notes ?? '')
    setChargesAsFound({
      discountPercent: '',
      discountReason: order.discountReason ?? '',
      discountRecommendedBy: order.discountRecommendedBy ?? '',
      notes: order.notes ?? '',
    })
  }

  const day = eventStart ? eventStart.slice(0, 10) : ''
  const { data: board } = useHallSlotBoard(hallId || undefined, day, day, order.id)
  const slots = board?.[0]?.slots ?? []

  /** Choosing a package brings its rate; the rate stays editable, because a
      counter often agrees something other than the list price. */
  const choosePackage = (versionId: string) => {
    setPackageVersionId(versionId)
    const chosen = sellable.find((one) => one.currentVersionId === versionId)
    setPricePerPerson(chosen?.basePricePerPerson == null ? '' : String(chosen.basePricePerPerson))
    // A different package offers different dishes, so what was picked off the
    // last one means nothing here.
    setMenuChoices(null)
  }

  /** Which package the chosen version belongs to, which is what the lines hang off. */
  const chosenPackageId = sellable.find((one) => one.currentVersionId === packageVersionId)?.id

  const sittingValue = Number(pricePerPerson || 0) * Number(guestCount || 0)

  /**
   * Whether a hall is part of this at all.
   *
   * <p>Food that travels is served wherever the customer is: there is no hall
   * to hold, nothing to clash with, and nothing to override. Asking about one
   * on a takeaway is three boxes of noise on a form somebody fills in at a
   * counter with a customer waiting.</p>
   */
  const hallApplies = !['OUTDOOR_CATERING', 'BULK_TAKEAWAY'].includes(order.serviceType)

  /**
   * Whether we go to them, and so whether this sitting has to say where.
   *
   * <p>Read off the line of business as the office set it up rather than
   * guessed from its name: a business that delivers its takeaway and one that
   * hands it over the counter are the same word on two different setups.</p>
   */
  const { data: serviceTypes } = useServiceTypes({ outletId: order.outletId })
  const delivers =
    (serviceTypes ?? []).find((one) => one.code === order.serviceType)?.needsDeliveryAddress
    ?? !hallApplies

  /**
   * Enough of an address to find the place.
   *
   * <p>A street or an area at the least. A landmark alone is how a driver ends
   * up ringing the office from a junction, and a pincode alone is a postal
   * sorting district, not a house.</p>
   */
  const somewhereToGo =
    Boolean(address.streetName?.trim())
    || Boolean(address.areaName?.trim())
    || Boolean(address.buildingName?.trim())
  const addressMissing = delivers && !somewhereToGo

  /**
   * How many this job may be taken for, said before a number is typed.
   *
   * <p>The branch's own answer where it has one, the company's otherwise. A
   * hall narrows it further: the service says what the business will take on,
   * the room says what it holds, and the tighter of the two is what matters.</p>
   */
  const { data: serviceLimits } = usePaxLimits(order.serviceType, order.outletId)
  const chosenHall = (halls ?? []).find((one) => one.id === hallId)
  const hallCeiling = chosenHall
    ? chosenHall.maxPax ?? (chosenHall.seatingCapacity > 0 ? chosenHall.seatingCapacity : null)
    : null
  const ceilings = [serviceLimits?.maxPax, hallCeiling].filter(
    (one): one is number => typeof one === 'number' && one > 0,
  )
  const floor = Math.max(serviceLimits?.minPax ?? 0, chosenHall?.minPax ?? 0) || null
  const ceiling = ceilings.length > 0 ? Math.min(...ceilings) : null
  const pax = Number(guestCount || 0)
  const paxOutOfRange =
    pax > 0 && ((floor != null && pax < floor) || (ceiling != null && pax > ceiling))
  const paxHelp = paxOutOfRange
    ? floor != null && pax < floor
      ? `This is taken for ${floor} or more`
      : `This is taken for up to ${ceiling}`
    : floor != null && ceiling != null
      ? `Between ${floor} and ${ceiling}`
      : floor != null
        ? `${floor} or more`
        : ceiling != null
          ? `Up to ${ceiling}`
          : 'How many are coming to this session'


  /**
   * The hours a sitting of that name runs, as the office set them up.
   *
   * <p>Breakfast is at seven and dinner is at eight, and that is a setting
   * rather than something to be typed on every booking. Picking the sitting
   * settles the hours; they can still be corrected once it is on the order.</p>
   */
  const minutesFor = (code: string) =>
    (sessionTypes ?? []).find((one) => one.code === code)?.durationMinutes || 240

  /**
   * The end, worked out from the start.
   *
   * <p>A sitting is a start and a length: breakfast is at seven and runs three
   * hours. Asking for the end as well was asking the same question twice and
   * inviting the two to disagree.</p>
   *
   * <p>A hall slot is the exception, and the reason the end still exists at
   * all. A slot is a pair of hours the hall is let for, so picking one sets
   * both and the sitting keeps what the slot says.</p>
   */
  const endFollowing = (start: string, code: string) => {
    if (!start) return ''
    const at = new Date(start)
    if (Number.isNaN(at.getTime())) return ''
    const ends = new Date(at.getTime() + minutesFor(code) * 60000)
    const pad = (value: number) => String(value).padStart(2, '0')
    return `${ends.getFullYear()}-${pad(ends.getMonth() + 1)}-${pad(ends.getDate())}T${pad(
      ends.getHours(),
    )}:${pad(ends.getMinutes())}`
  }

  /**
   * The day this sitting is on.
   *
   * <p>The order's day, or the day the sitting is already on where it has one.
   * It is not asked for: every sitting of a wedding is on the wedding day, and
   * a sitting that has moved is moved from the order rather than by retyping a
   * date into a session form.</p>
   */
  const sittingDay = eventStart ? eventStart.slice(0, 10) : order.eventDate

  /** Typing a start moves the end with it, unless a slot is holding both. */
  const startAt = (value: string) => {
    setEventStart(value)
    if (!hallSlotId) {
      setEventEnd(endFollowing(value, mealPeriod))
    }
  }

  /** Where it ends, said rather than asked - and said plainly when it is tomorrow. */
  const runsTo = (() => {
    if (!eventEnd) return 'When this sitting starts'
    const overnight = eventEnd.slice(0, 10) !== sittingDay
    return `Runs to ${eventEnd.slice(11, 16)}${overnight ? ' next day' : ''}${
      hallSlotId ? ', from the slot' : ''
    }`
  })()

  const hoursFor = (code: string) => {
    const type = (sessionTypes ?? []).find((one) => one.code === code)
    if (!type?.startTime) return null
    const start = new Date(`${order.eventDate}T${type.startTime.slice(0, 5)}`)
    const end = new Date(start.getTime() + (type.durationMinutes || 240) * 60000)
    const stamp = (when: Date) =>
      `${when.getFullYear()}-${String(when.getMonth() + 1).padStart(2, '0')}-${String(
        when.getDate(),
      ).padStart(2, '0')}T${String(when.getHours()).padStart(2, '0')}:${String(
        when.getMinutes(),
      ).padStart(2, '0')}`
    return { start: stamp(start), end: stamp(end) }
  }

  /** What to call it: the meal it is, else the occasion the order is for. */
  const sittingName =
    (sessionTypes ?? []).find((one) => one.code === mealPeriod)?.name ?? order.eventType ?? ""

  const body = {
    // Named after the sitting it is, since nobody types a name any more. An
    // existing one is kept: a sitting somebody named by hand before today
    // should not be renamed behind them.
    eventName: eventName || sittingName || undefined,
    mealPeriod: mealPeriod || undefined,
    serviceStyle: serviceStyle || undefined,
    hallId: hallId || undefined,
    hallSlotId: hallSlotId || undefined,
    eventStart: eventStart ? new Date(eventStart).toISOString() : undefined,
    eventEnd: eventEnd ? new Date(eventEnd).toISOString() : undefined,
    guestCount: Number(guestCount || 0),
    address: delivers ? address : undefined,
    deliveryAddress: delivers ? findingIt.trim() || undefined : undefined,
    venueDistanceKm: delivers && venueDistanceKm !== '' ? Number(venueDistanceKm) : undefined,
    venueMapUrl: delivers ? venueMapUrl.trim() || undefined : undefined,
    packageVersionId: packageVersionId || undefined,
    pricePerPerson: pricePerPerson === '' ? undefined : Number(pricePerPerson),
    overrideReason: overrideReason.trim() || undefined,
    // Only once the lines have actually been read. Sending an empty list before
    // then would tell the server the choosing was done and came to nothing,
    // which would strip a menu somebody never looked at.
    menuChoices: menuChoices ?? undefined,
  }

  /** Only what somebody actually altered on the last stage. */
  const chargesWanted =
    discountPercent !== chargesAsFound.discountPercent
    || discountReason.trim() !== chargesAsFound.discountReason.trim()
    || discountRecommendedBy.trim() !== chargesAsFound.discountRecommendedBy.trim()
    || notes.trim() !== chargesAsFound.notes.trim()

  /**
   * How the job is served, where that is the order's answer rather than this
   * sitting's: one lorry goes to one function and one roster covers it.
   */
  const jobWanted =
    delivers
    && (fulfilmentMode !== (order.fulfilmentMode ?? 'DELIVERY')
      || manpowerRequired !== (order.manpowerRequired ?? true))

  /** The charges stage only speaks if it was given something to say. */
  const finish = () => {
    if (jobWanted) {
      editOrder.mutate({
        id: order.id,
        body: { fulfilmentMode, manpowerRequired },
      })
    }
    if (!chargesWanted) {
      props.onClose()
      return
    }
    charges.mutate(
      {
        orderId: order.id,
        body: {
          discountPercent: discountPercent ? Number(discountPercent) : undefined,
          discountReason: discountReason.trim() || undefined,
          discountRecommendedBy: discountRecommendedBy.trim() || undefined,
          notes: notes.trim() || undefined,
        },
      },
      { onSuccess: props.onClose },
    )
  }

  const submit = () => {
    if (adding) {
      add.mutate({ orderId: order.id, body }, { onSuccess: finish })
      return
    }
    if (!session) return
    // The same ending as adding: the sitting first, then whatever the last
    // stage was given. Without this, a discount typed while correcting a
    // session would be thrown away on the way out.
    edit.mutate({ orderId: order.id, sessionId: session.id, body }, { onSuccess: finish })
  }

  return (
    <Dialog open={Boolean(session) || adding} onClose={props.onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{adding ? 'Add a session' : 'This session'}</DialogTitle>
      <DialogContent dividers>
        <ErrorBanner error={edit.error ?? add.error ?? charges.error} />
        {/* Walked in order when a sitting is being added, because none of it
            exists yet. Not shown when correcting one that already exists:
            its package, menu and charges are set from order edit, which
            already walks those same steps for the whole order, so this
            screen is left with the one thing it alone does - the hall and
            its slot. */}
        {adding ? (
        <Stepper activeStep={stage} sx={{ mb: 2 }}>
          {STAGES.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        ) : null}
        {/* Said for the stage being looked at. One sentence describing the
            hours, left standing over the discount, teaches nothing. */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {stage === 0
            ? adding
              ? 'Which sitting of the day, when it starts, for how many, and how it is served. How long it runs comes with the sitting, and the hall comes from the order.'
              : hallApplies
                ? 'Where this sitting is held. Picking a hall and a slot sets the hours and re-checks the booking; your own other sessions never count as a clash.'
                : 'This one travels to the customer, so there is no hall to hold or hours to set here.'
            : stage === 1
              ? 'What this session is sold on, and at what a head. Only the packages written for this sitting are offered. Tick what the customer is choosing; what the package always includes is already on.'
              : 'What the whole order comes to. A discount and any notes belong to the order rather than to this one session, and are asked for here because this is where the conversation ends.'}
        </Typography>

        {stage === 0 ? (
        <Grid container spacing={2}>
          {/* Asked only while adding: once a sitting exists, its meal
              period, start time, head count and service style are set from
              here, not corrected from here again - the hall and its slot
              below are what this screen is for once a sitting is on the
              order. */}
          {adding ? (
          <>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Session"
              value={mealPeriod}
              onChange={(event) => {
                setMealPeriod(event.target.value)
                // The hours come with it. Correcting them afterwards is a
                // change to a sitting that exists, not a question to answer
                // before it does.
                const hours = adding ? hoursFor(event.target.value) : null
                if (hours) {
                  setEventStart(hours.start)
                  setEventEnd(hours.end)
                }
                // A breakfast menu on a dinner is not a choice anybody meant to
                // make, so changing the sitting lets go of a package written
                // for the one before it.
                setPackageVersionId('')
                setPricePerPerson('')
                setMenuChoices(null)
              }}
              helperText="The hours come with it"
            >
              <MenuItem value="">Not said</MenuItem>
              {(sessionTypes ?? []).map((one) => (
                <MenuItem key={one.id} value={one.code}>
                  {one.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* Beside the sitting that sets them. They arrive filled in from
              the sitting picked above, and are still a time somebody can
              disagree with - a reception that runs late is the rule. */}
          <Grid item xs={12} sm={6}>
            {/* A time, not a date and a time. The day is the order's - a
                sitting of a wedding is on the wedding day - and typing it
                again on every sitting was a chance to type it wrong. */}
            <TimeField
              label="Starts"
              value={eventStart.slice(11, 16)}
              onChange={(value) => startAt(value ? `${sittingDay}T${value}` : '')}
              helperText={runsTo}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label="Guests"
              value={guestCount}
              onChange={(event) => setGuestCount(event.target.value)}
              // Said before it is typed and while it is being typed. The server
              // refuses the same numbers; being told on the way out is being
              // told too late.
              error={paxOutOfRange}
              helperText={paxHelp}
              inputProps={{
                min: floor ?? 1,
                ...(ceiling != null ? { max: ceiling } : {}),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {/* How it is served, which decides the staff and the ware. It was
                on the order and never asked for on the sitting, though a
                reception is served one way and the dinner after it another. */}
            <TextField
              select
              fullWidth
              label="Served as"
              value={serviceStyle}
              onChange={(event) => setServiceStyle(event.target.value)}
              helperText="Leaf service or a buffet"
            >
              <MenuItem value="">Not said</MenuItem>
              <MenuItem value="LEAF">Leaf service</MenuItem>
              <MenuItem value="BUFFET">Buffet</MenuItem>
            </TextField>
          </Grid>
          </>
          ) : null}

          {/* Where the food is going. On a job that travels this is not a
              detail for later: it is the difference between a job the crew can
              do and a lorry with nowhere to take it. */}
          {delivers ? (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Where it is served
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {orderAddress
                    ? 'Taken from this order; correct it where this sitting is somewhere else.'
                    : 'This session travels to the customer, so it needs an address.'}
                </Typography>
              </Grid>
              <DeliveryAddressFields
                value={address}
                onChange={(patch) => setAddress((current) => ({ ...current, ...patch }))}
              />
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Anything else about finding the place"
                  value={findingIt}
                  onChange={(event) => setFindingIt(event.target.value)}
                  helperText="Optional. The address above is what prints on the documents."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Distance"
                  value={venueDistanceKm}
                  onChange={(event) => setVenueDistanceKm(event.target.value)}
                  helperText="Kilometres, one way"
                  inputProps={{ min: 0, step: 0.5 }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Google Maps location"
                  value={venueMapUrl}
                  onChange={(event) => setVenueMapUrl(event.target.value)}
                  placeholder="Paste the map link, or leave it blank to search the address"
                  helperText={
                    venueMapUrl
                      ? 'The driver is sent this pin'
                      : 'Without a pin the driver gets only the written address'
                  }
                />
              </Grid>
              {/* The job's rather than this sitting's: one lorry goes to one
                  function, and one roster covers it. Said so on the form, so
                  nobody sets it per meal and expects two answers. */}
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Delivery or pickup"
                  value={fulfilmentMode}
                  onChange={(event) => setFulfilmentMode(event.target.value)}
                  helperText={
                    fulfilmentMode === 'PICKUP'
                      ? 'The customer collects; no vehicle is sent'
                      : 'We take the food to the venue'
                  }
                >
                  <MenuItem value="DELIVERY">Delivery</MenuItem>
                  <MenuItem value="PICKUP">Pickup</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={8}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={manpowerRequired}
                      onChange={(event) => setManpowerRequired(event.target.checked)}
                    />
                  }
                  label="We send staff"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  {manpowerRequired
                    ? 'A roster is worked out for this job when the order is confirmed.'
                    : 'Food only — the customer serves it. Nothing is rostered and no shortage is raised.'}
                  {' '}For the whole order, not this session alone.
                </Typography>
              </Grid>
              {addressMissing ? (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    Give at least a street or an area. A landmark on its own is how a driver ends up
                    ringing the office from a junction.
                  </Alert>
                </Grid>
              ) : null}
            </>
          ) : null}

        </Grid>
        ) : null}

        {stage === 1 ? (
        <Grid container spacing={2}>
          {/* Sold by the head: the package says what a head costs, and the
              head count says how many. Both are settled here, because on an
              order taken at the counter neither was known when it was
              taken. */}
          {sellable.length === 0 ? (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                No package is sold by the head for this line of business. The session is priced
                from the items listed against it.
              </Typography>
            </Grid>
          ) : (
            <>
              <Grid item xs={12} sm={7}>
                <TextField
                  select
                  fullWidth
                  label="Package"
                  value={packageVersionId}
                  onChange={(event) => choosePackage(event.target.value)}
                  helperText={
                    session?.packageVersionId && packageVersionId !== session.packageVersionId
                      ? 'Changing the package reprices this session'
                      : 'What this session is sold on'
                  }
                >
                  <MenuItem value="">Not sold by the head</MenuItem>
                  {/* A sitting already sold on a package the narrowing does not
                      offer keeps it on the list. Dropping it silently would
                      unprice the sitting the moment somebody opened it to
                      correct a head count. */}
                  {session?.packageVersionId
                  && packageVersionId === session.packageVersionId
                  && !sellable.some((one) => one.currentVersionId === packageVersionId) ? (
                    <MenuItem value={session.packageVersionId}>
                      As sold, on another sitting&apos;s package
                    </MenuItem>
                  ) : null}
                  {sellable.map((one) => (
                    <MenuItem key={one.id} value={one.currentVersionId}>
                      {one.name}
                      {one.basePricePerPerson == null
                        ? ''
                        : ` — ${formatMoney(one.basePricePerPerson)} a head`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  type="number"
                  label="Rate a head"
                  value={pricePerPerson}
                  onChange={(event) => setPricePerPerson(event.target.value)}
                  disabled={!packageVersionId}
                  helperText={
                    sittingValue > 0 ? `Comes to ${formatMoney(sittingValue)}` : 'As agreed'
                  }
                />
              </Grid>
              <Grid item xs={12}>
                {/* The choosing, here rather than on another tab afterwards.
                    What the package always includes is shown ticked and cannot
                    be dropped; the rest is what the customer is deciding. */}
                <PackageMenuChoices
                  packageId={chosenPackageId}
                  value={menuChoices ?? []}
                  onChange={setMenuChoices}
                />
              </Grid>
            </>
          )}
        </Grid>
        ) : null}

        {/* Adding, none of this is asked. The hall was settled when the order
            was taken and every sitting of the day is in it; the hours come
            with the sitting that was picked. Correcting, all of it is here,
            because moving a sitting to another hall or another hour is the
            whole reason for opening it. */}
        {stage === 0 && adding && hallApplies && orderHallId ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            In {(halls ?? []).find((one) => one.id === orderHallId)?.name ?? 'the hall'}, from the
            order. Move it to another hall afterwards from the session itself.
          </Typography>
        ) : null}

        {stage === 0 && !adding ? (
        <Grid container spacing={2} sx={{ mt: 0 }}>

          {hallApplies ? (
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Hall"
              value={hallId}
              onChange={(event) => {
                setHallId(event.target.value)
                setHallSlotId('')
              }}
            >
              <MenuItem value="">No hall — outdoor</MenuItem>
              {(halls ?? []).map((hall) => (
                <MenuItem key={hall.id} value={hall.id}>
                  {hall.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          ) : null}

          {hallId && slots.length > 0 ? (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Slot on {day} — picking one sets the hours
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                {slots.map((slot) => {
                  const chosen = hallSlotId === slot.slotId
                  // A slot this very sitting holds reads as taken; that is not a
                  // clash with anyone, so it stays selectable and unmarked.
                  const heldByThisOrder = slot.heldBy === order.orderNumber
                  return (
                    <Chip
                      key={slot.slotId}
                      size="small"
                      variant={chosen ? 'filled' : 'outlined'}
                      color={chosen ? 'primary' : slot.available || heldByThisOrder ? 'default' : 'error'}
                      label={`${slot.name} ${formatSlotHours(slot.startTime, slot.endTime)}${
                        slot.available || heldByThisOrder ? '' : ' · taken'
                      }`}
                      title={
                        slot.available || heldByThisOrder
                          ? `${formatSlotHours(slot.startTime, slot.endTime)} — ${formatMoney(slot.rentalCharge)}`
                          : `${formatSlotHours(slot.startTime, slot.endTime)} — held by ${slot.heldBy}`
                      }
                      onClick={() => {
                        setHallSlotId(chosen ? '' : slot.slotId)
                        if (!chosen) {
                          setEventStart(`${day}T${slot.startTime.slice(0, 5)}`)
                          const endsNextDay = slot.endTime < slot.startTime
                          const endDay = endsNextDay
                            ? format(
                                new Date(new Date(`${day}T00:00:00`).getTime() + 86400000),
                                'yyyy-MM-dd',
                              )
                            : day
                          setEventEnd(`${endDay}T${slot.endTime.slice(0, 5)}`)
                        }
                      }}
                    />
                  )
                })}
              </Stack>
            </Grid>
          ) : null}


          {/* Only where there is a hall to push past. Nothing else in this
              form can clash with anybody. */}
          {hallApplies && hallId ? (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Override reason"
              value={overrideReason}
              onChange={(event) => setOverrideReason(event.target.value)}
              helperText="Only needed if the hall is already taken at the new time"
            />
          </Grid>
          ) : null}
        </Grid>
        ) : null}

        {stage === 2 ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Money off this order, and anything it should be remembered by. Both belong to the
                order rather than to this session, and both can be left empty.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              {/* Chosen off the list the business gives, or typed. The list is
                  what stops the same job being given three at one desk and ten
                  at another. */}
              <DiscountField
                value={discountPercent}
                onChange={setDiscountPercent}
                onReason={(reason) => {
                  if (!discountReason.trim()) setDiscountReason(reason)
                }}
                helperText="Beyond your authority it is held for whoever may agree it, not applied yet"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Why"
                value={discountReason}
                onChange={(event) => setDiscountReason(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Recommended by"
                value={discountRecommendedBy}
                onChange={(event) => setDiscountRecommendedBy(event.target.value)}
                helperText="The line the approver reads"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </Grid>
          </Grid>
        ) : null}

        {stage === 0 && hallApplies ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          The setup and cleanup buffers are worked out again from wherever the session lands.
        </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        {/* Adding, the stages are walked in order and the session is written
            only at the end of them - one save, so a session cannot land with
            its price still being argued about two stages later.

            Correcting, there is only the one stage left, so Save stands on
            it directly. */}
        {adding && stage > 0 ? (
          <Button onClick={() => setStage((current) => current - 1)}>Back</Button>
        ) : null}
        {adding && stage < STAGES.length - 1 ? (
          <Button
            variant="contained"
            onClick={() => setStage((current) => current + 1)}
            disabled={stage === 0 && (!eventStart || !eventEnd || paxOutOfRange || addressMissing)}
          >
            Continue
          </Button>
        ) : null}
        {!adding || stage === STAGES.length - 1 ? (
          <Button
            variant="contained"
            onClick={submit}
            disabled={
              edit.isPending || add.isPending || charges.isPending || !eventStart || !eventEnd
              || paxOutOfRange || addressMissing
            }
          >
            {adding ? 'Add it' : 'Save'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  )
}
